import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  MapPin, DollarSign, Upload, Loader2, Send, FileText, X, CheckCircle2, ShieldCheck, Clock, HandCoins,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LOGO = "/logo.png";
const TEAL = "#27BEAA";
const INP = "bg-white/[0.04] border-white/15 text-white placeholder:text-white/30 focus-visible:ring-[#27BEAA]";

const FF = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-sm text-white/70">{label}</Label>{children}</div>
);

export default function SellYourLandPublic() {
  const [sp] = useSearchParams();
  const leadId = sp.get("lead") || "";
  const resource = sp.get("resource") || "";

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const fileRef = useRef();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    address: "", apn: "", city: "", state: "", postal_code: "",
    acreage: "", lot_size_sqft: "", asking_price: "",
    zoning: "", road_access: "", utilities: "", notes: "",
  });

  useEffect(() => {
    document.title = "Sell Your Land — ARQITEK";
    if (leadId && resource) {
      axios.get(`${BACKEND}/api/public/sell-land/context`, { params: { lead: leadId, resource } })
        .then(({ data }) => {
          if (data.matched) {
            setForm((p) => ({
              ...p,
              address: data.address || p.address,
              city: data.city || p.city,
              state: data.state || p.state,
              postal_code: data.postal_code || p.postal_code,
              apn: data.apn || p.apn,
            }));
          }
        }).catch(() => {});
    }
  }, [leadId, resource]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const onFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    for (const file of picked) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await axios.post(`${BACKEND}/api/public/sell-land/upload`, fd);
        setFiles((prev) => [...prev, data]);
      } catch { /* skip */ }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!form.email.trim() && !form.phone.trim()) { alert("Please add an email or phone so we can reach you."); return; }
    if (!form.address.trim() && !form.apn.trim()) { alert("Please add your property address or APN."); return; }
    setSubmitting(true);
    try {
      await axios.post(`${BACKEND}/api/public/sell-land/submit`, { ...form, files, lead_id: leadId, resource });
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      alert(e.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#08100E] flex items-center justify-center p-6" data-testid="sell-land-page">
        <div className="max-w-md text-center rounded-2xl border border-white/10 bg-white/[0.02] p-10" data-testid="sell-land-success">
          <CheckCircle2 className="h-14 w-14 mx-auto mb-4" style={{ color: TEAL }} />
          <h2 className="text-2xl font-semibold text-white">You're all set</h2>
          <p className="text-white/50 mt-2">Thanks — we've received your property and asking price. Our team will review it and reach out to you shortly with next steps.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#08100E] text-white" data-testid="sell-land-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div
          className="absolute inset-0 opacity-[0.18]"
          style={{ background: `radial-gradient(1200px 500px at 20% -10%, ${TEAL}, transparent 60%)` }}
        />
        <div className="relative max-w-5xl mx-auto px-6 pt-10 pb-14">
          <img src={LOGO} alt="ARQITEK" className="h-7 w-auto object-contain" />
          <div className="mt-12 max-w-3xl">
            <span className="inline-block text-[11px] uppercase tracking-[0.25em] text-[#27BEAA] mb-4">Sell Your Land</span>
            <h1 className="text-4xl sm:text-6xl font-bold tracking-tight leading-[1.05]">
              Name your price.<br />We'll make it simple.
            </h1>
            <p className="text-white/60 mt-6 text-base sm:text-lg max-w-2xl leading-relaxed">
              Tell us about your land and what you'd sell it for. No obligation, no fees, no pressure —
              just a fast, fair conversation with a local developer.
            </p>
            <div className="flex flex-wrap gap-6 mt-8 text-sm text-white/70">
              <span className="flex items-center gap-2"><HandCoins className="h-4 w-4" style={{ color: TEAL }} /> Fair cash offer</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" style={{ color: TEAL }} /> Quick response</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" style={{ color: TEAL }} /> Reviewed confidentially</span>
            </div>
            <Button
              size="lg" className="mt-9 font-medium" style={{ backgroundColor: TEAL, color: "#08100E" }}
              data-testid="sell-land-start-btn"
              onClick={() => document.getElementById("sell-form")?.scrollIntoView({ behavior: "smooth" })}
            >
              <Send className="h-4 w-4 mr-2" /> Get my offer
            </Button>
          </div>
        </div>
      </div>

      {/* Form */}
      <main id="sell-form" className="max-w-3xl mx-auto px-6 py-14">
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8" data-testid="sell-land-form">
          <h2 className="text-2xl font-semibold">Tell us about your land</h2>
          <p className="text-white/50 mt-1.5 text-sm">Only a way to reach you and your property address (or APN) are required — leave the rest blank if you're not sure.</p>

          <div className="mt-7 space-y-6">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" style={{ color: TEAL }} /> Your contact</div>
              <div className="grid gap-4 sm:grid-cols-3">
                <FF label="Your name"><Input className={INP} data-testid="syl-name" value={form.name} onChange={(e) => set("name", e.target.value)} /></FF>
                <FF label="Email"><Input className={INP} type="email" data-testid="syl-email" value={form.email} onChange={(e) => set("email", e.target.value)} /></FF>
                <FF label="Phone"><Input className={INP} data-testid="syl-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} /></FF>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><MapPin className="h-3.5 w-3.5" style={{ color: TEAL }} /> The property</div>
              <FF label="Property address *"><Input className={INP} data-testid="syl-address" value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Address or nearest cross street" /></FF>
              <div className="grid gap-4 sm:grid-cols-2 mt-4">
                <FF label="APN (parcel number, if known)"><Input className={INP} data-testid="syl-apn" value={form.apn} onChange={(e) => set("apn", e.target.value)} /></FF>
                <FF label="City"><Input className={INP} value={form.city} onChange={(e) => set("city", e.target.value)} /></FF>
                <FF label="State"><Input className={INP} value={form.state} onChange={(e) => set("state", e.target.value)} /></FF>
                <FF label="ZIP"><Input className={INP} value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} /></FF>
                <FF label="Acreage"><Input className={INP} value={form.acreage} onChange={(e) => set("acreage", e.target.value)} /></FF>
                <FF label="Lot size (SF)"><Input className={INP} value={form.lot_size_sqft} onChange={(e) => set("lot_size_sqft", e.target.value)} /></FF>
                <FF label="Zoning (if known)"><Input className={INP} value={form.zoning} onChange={(e) => set("zoning", e.target.value)} /></FF>
                <FF label="Road / access"><Input className={INP} value={form.road_access} onChange={(e) => set("road_access", e.target.value)} /></FF>
                <FF label="Utilities (if known)"><Input className={INP} value={form.utilities} onChange={(e) => set("utilities", e.target.value)} /></FF>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <div className="text-[11px] uppercase tracking-wider text-white/40 mb-3 flex items-center gap-2"><DollarSign className="h-3.5 w-3.5" style={{ color: TEAL }} /> Your price</div>
              <FF label="What would you sell it for?">
                <Input className={`${INP} text-lg`} data-testid="syl-price" value={form.asking_price} onChange={(e) => set("asking_price", e.target.value)} placeholder="$ Asking price" />
              </FF>
              <div className="mt-4">
                <FF label="Anything else we should know?"><Textarea className={INP} rows={3} data-testid="syl-notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></FF>
              </div>
            </div>

            <div className="border-t border-white/10 pt-6">
              <Label className="text-sm text-white/70">Photos / documents (optional)</Label>
              <div className="mt-2 flex items-center gap-3">
                <input ref={fileRef} type="file" multiple className="hidden" onChange={onFiles} data-testid="syl-file-input" />
                <Button type="button" variant="outline" size="sm" className="border-white/15 bg-white/[0.04] text-white hover:bg-white/10 hover:text-white" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload
                </Button>
                <span className="text-xs text-white/30">Photos, plats, or a survey — optional</span>
              </div>
              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-white/[0.06] text-white/80">
                      <FileText className="h-3 w-3" /> {f.filename}
                      <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <Button
              size="lg" className="w-full font-medium" style={{ backgroundColor: TEAL, color: "#08100E" }}
              data-testid="syl-submit-btn" onClick={submit} disabled={submitting}
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Send my details
            </Button>
          </div>
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between text-white/30 text-xs">
          <img src={LOGO} alt="ARQITEK" className="h-5 w-auto object-contain opacity-60" />
          <span>© {new Date().getFullYear()} ARQITEK. Submissions reviewed confidentially.</span>
        </div>
      </footer>
    </div>
  );
}
